"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/rac/button";
import { Tooltip, TooltipTrigger } from "@/components/rac/tooltip";
import { motion } from "framer-motion";
import { useRef } from "react";
import { ChatIcon, CloseIcon } from "./icons";
interface FABTriggerProps {
  isOpen: boolean;
  className?: string;
}

/**
 * Floating Action Button trigger for the chatbot
 * Draggable and shows different icons based on chat state
 */
export function FABTrigger(t0) {
  const $ = _c(19);
  const {
    isOpen,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const isDraggingRef = useRef(false);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      x: 0,
      y: 0
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const dragStartPosRef = useRef(t2);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e => {
      if (isDraggingRef.current) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const handleClick = t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = e_0 => {
      dragStartPosRef.current = {
        x: e_0.clientX,
        y: e_0.clientY
      };
    };
    t5 = e_1 => {
      const distance = Math.hypot(e_1.clientX - dragStartPosRef.current.x, e_1.clientY - dragStartPosRef.current.y);
      if (distance > 5) {
        isDraggingRef.current = true;
        setTimeout(() => {
          isDraggingRef.current = false;
        }, 200);
      }
    };
    t6 = {
      cursor: "grabbing",
      scale: 1.05
    };
    t7 = {
      scale: 1.05
    };
    $[2] = t4;
    $[3] = t5;
    $[4] = t6;
    $[5] = t7;
  } else {
    t4 = $[2];
    t5 = $[3];
    t6 = $[4];
    t7 = $[5];
  }
  const t8 = `fixed bottom-6 right-6 z-[9998] ${className}`;
  const t9 = isOpen ? "Close Chat" : "Open Chat";
  let t10;
  if ($[6] !== isOpen) {
    t10 = isOpen ? <CloseIcon className="w-6 h-6" /> : <ChatIcon className="w-8 h-8" />;
    $[6] = isOpen;
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  let t11;
  if ($[8] !== t10 || $[9] !== t9) {
    t11 = <button onClick={handleClick} id="bp-toggle-chat" className="w-14 h-14 rounded-full bg-[#E0454B] text-white shadow-lg hover:bg-[#c93e43] transition-all duration-300 flex items-center justify-center focus:outline-none focus:ring-4 focus:ring-[#E0454B]/30" aria-label={t9}>{t10}</button>;
    $[8] = t10;
    $[9] = t9;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  const t12 = isOpen ? "Close Chat" : "Chat with us";
  let t13;
  if ($[11] !== t12) {
    t13 = <Tooltip>{t12}</Tooltip>;
    $[11] = t12;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  let t14;
  if ($[13] !== t11 || $[14] !== t13) {
    t14 = <TooltipTrigger>{t11}{t13}</TooltipTrigger>;
    $[13] = t11;
    $[14] = t13;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== t14 || $[17] !== t8) {
    t15 = <motion.div drag={true} dragMomentum={false} onPointerDown={t4} onPointerUp={t5} whileDrag={t6} whileHover={t7} className={t8}>{t14}</motion.div>;
    $[16] = t14;
    $[17] = t8;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  return t15;
}
interface CTAButtonTriggerProps {}

/**
 * Small circular button for CTA bars
 */
export function CTAButtonTrigger(t0) {
  const $ = _c(1);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <TooltipTrigger><button type="button" className="w-10 h-10 rounded-full border border-gray-300 bg-white hover:bg-gray-50 transition-colors flex items-center justify-center cursor-pointer outline-none focus:ring-2 focus:ring-primary-500" aria-label="Chat with us" id="bp-toggle-chat"><ChatIcon className="w-5 h-5" stroke="currentColor" /></button><Tooltip>Chat with us</Tooltip></TooltipTrigger>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  return t1;
}

/**
 * Menu item trigger for dropdown menus
 */
export function MoreMenuTrigger() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <TooltipTrigger><button type="button" className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-100 flex items-center" id="bp-toggle-chat"><ChatIcon className="w-4 h-4 mr-3" stroke="currentColor" />Chat with us</button><Tooltip>Open chat</Tooltip></TooltipTrigger>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
interface BigButtonTriggerProps {
  className?: string;
}

/**
 * Large prominent button for contact page
 */
export function BigButtonTrigger(t0) {
  const $ = _c(5);
  const {
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChatIcon className="w-8 h-8 group-hover:animate-pulse" stroke="currentColor" />;
    t3 = <span>Start Live Chat</span>;
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <TooltipTrigger><Button variant="bareshell" className="w-full bg-gradient-to-r from-primary-600 to-blue-600 hover:from-primary-700 hover:to-blue-700 text-white px-8 py-6 rounded-2xl font-semibold text-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl flex items-center justify-center gap-4 group" id="bp-toggle-chat">{t2}{t3}<svg className="w-6 h-6 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg></Button><Tooltip>Chat with our AI assistant Sia</Tooltip></TooltipTrigger>;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== className) {
    t5 = <div className={className}>{t4}</div>;
    $[3] = className;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  return t5;
}